import { useMemo } from 'react'
import {
  Box,
  Stack,
  Text,
  useMantineTheme,
} from '@mantine/core'
import {
  ArcElement,
  Chart as ChartJS,
} from 'chart.js'
import { Doughnut } from 'react-chartjs-2'

ChartJS.register(ArcElement)

interface RealTimeFlowGaugeProps {
  value?: number
  status?: number
  maxValue: number
  averageValue?: number
}

const clampPercent = (percent: number) => Math.min(100, Math.max(0, percent))

function RealTimeFlowGauge({
                             value,
                             status,
                             maxValue,
                             averageValue,
                           }: RealTimeFlowGaugeProps) {
  const theme = useMantineTheme()
  const percent = clampPercent(
    value === undefined
      ? 0
      : value > 0
        ? (Math.min(maxValue, value) / maxValue) * 100
        : 1,
  )
  const displayText = (() => {
    if (status === 1) return value !== undefined ? value.toFixed(2) : '0'
    if (status === 2) return 'Connecting...'
    if (status === 0) return 'No data received'
    return 'Ready'
  })()
  const activeColor = theme.colors.blue[6]
  const backgroundColor = theme.colors.gray[3]

  const data = useMemo(() => ({
    datasets: [{
      data: [percent, 100 - percent],
      backgroundColor: [activeColor, backgroundColor],
      borderWidth: 0,
      circular: true,
    }],
  }), [percent, activeColor, backgroundColor])

  const options = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    rotation: -135,
    circumference: 270,
    cutout: '75%',
    events: [],
    animation: true as const,
  }), [])

  return (
    <Box
      className={'rtf-gauge'}
      role={status === 1 ? 'meter' : 'status'}
      aria-valuemin={status === 1 ? 0 : undefined}
      aria-valuemax={status === 1 ? maxValue : undefined}
      aria-valuenow={status === 1 ? value ?? 0 : undefined}
      aria-label={'Real time flow'}
    >
      <Doughnut
        data={data}
        options={options}
        aria-hidden={'true'}
      />

      <Stack className={'rtf-gauge-label'} gap={0} align={'center'}>
        <Text
          c={status === 1 ? activeColor : 'gray'}
          size={status === 1 ? 'xl' : 'md'}
          fw={500}
        >
          {displayText}
        </Text>
        {status === 1 ? (
          <Text size={'xs'} c={'dimmed'}>
            l/min
          </Text>
        ) : null}
        {averageValue !== undefined ? (
          <Text size={'xs'} c={'dimmed'}>
            Avg {averageValue.toFixed(2)} l/min
          </Text>
        ) : null}
      </Stack>
    </Box>
  )
}

export default RealTimeFlowGauge
